import { removeFromArray } from '@standardnotes/utils';
import { isValidProtectionSessionLength } from '../Protection/isValidProtectionSessionLength';
import { AbstractService, Challenge, ChallengeReason, ChallengeValidation, ChallengePrompt, ChallengeStrings, } from '@standardnotes/services';
import { ChallengeOperation } from './ChallengeOperation';
const clearChallengeObserver = (observer) => {
    observer.onCancel = undefined;
    observer.onComplete = undefined;
    observer.onValidValue = undefined;
    observer.onInvalidValue = undefined;
    observer.onNonvalidatedSubmit = undefined;
};
/**
 * The challenge service creates, updates and keeps track of running challenge operations.
 */
export class ChallengeService extends AbstractService {
    constructor(storageService, encryptionService, internalEventBus) {
        super(internalEventBus);
        this.storageService = storageService;
        this.encryptionService = encryptionService;
        this.internalEventBus = internalEventBus;
        this.challengeOperations = {};
        this.challengeObservers = {};
    }
    deinit() {
        ;
        this.storageService = undefined;
        this.encryptionService = undefined;
        this.sendChallenge = undefined;
        this.challengeOperations = undefined;
        this.challengeObservers = undefined;
        super.deinit();
    }
    promptForChallengeResponse(challenge) {
        return new Promise((resolve) => {
            this.createOrGetChallengeOperation(challenge, resolve);
            this.sendChallenge(challenge);
        });
    }
    createChallenge(prompts, reason, cancelable, heading, subheading) {
        return new Challenge(prompts, reason, cancelable, heading, subheading);
    }
    async validateChallengeValue(value) {
        switch (value.prompt.validation) {
            case ChallengeValidation.LocalPasscode:
                return this.encryptionService.validatePasscode(value.value);
            case ChallengeValidation.AccountPassword:
                return this.encryptionService.validateAccountPassword(value.value);
            case ChallengeValidation.Biometric:
                return { valid: value.value === true };
            case ChallengeValidation.Authenticator:
                return { valid: 'id' in value.value };
            case ChallengeValidation.ProtectionSessionDuration:
                return { valid: isValidProtectionSessionLength(value.value) };
            default:
                throw Error(`Unhandled validation mode ${value.prompt.validation}`);
        }
    }
    async promptForCorrectPasscode(reason) {
        const challenge = new Challenge([new ChallengePrompt(ChallengeValidation.LocalPasscode)], reason, true);
        const response = await this.promptForChallengeResponse(challenge);
        if (!response) {
            return undefined;
        }
        const value = response.getValueForType(ChallengeValidation.LocalPasscode);
        return value.value;
    }
    async promptForAccountPassword() {
        var _a;
        if (!this.encryptionService.hasAccount()) {
            throw Error('Requiring account password for challenge with no account');
        }
        const response = await this.promptForChallengeResponse(new Challenge([new ChallengePrompt(ChallengeValidation.AccountPassword)], ChallengeReason.Custom, true, ChallengeStrings.EnterAccountPassword));
        return (_a = response === null || response === void 0 ? void 0 : response.getValueForType(ChallengeValidation.AccountPassword)) === null || _a === void 0 ? void 0 : _a.value;
    }
    /**
     * Returns the wrapping key for operations that require resaving the root key
     * (changing the account password, signing in, registering, or upgrading protocol)
     * Returns empty object if no passcode is configured.
     * Otherwise returns {cancled: true} if the operation is canceled, or
     * {wrappingKey} with the result.
     * @param passcode - If the consumer already has access to the passcode,
     * they can pass it here so that the user is not prompted again.
     */
    async getWrappingKeyIfApplicable(passcode) {
        if (!this.encryptionService.hasPasscode()) {
            return {};
        }
        if (!passcode) {
            passcode = await this.promptForCorrectPasscode(ChallengeReason.ResaveRootKey);
            if (!passcode) {
                return { canceled: true };
            }
        }
        const wrappingKey = await this.encryptionService.computeWrappingKey(passcode);
        return { wrappingKey };
    }
    addChallengeObserver(challenge, observer) {
        const observers = this.challengeObservers[challenge.id] || [];
        observers.push(observer);
        this.challengeObservers[challenge.id] = observers;
        return () => {
            clearChallengeObserver(observer);
            removeFromArray(observers, observer);
        };
    }
    createOrGetChallengeOperation(challenge, resolve) {
        let operation = this.getChallengeOperation(challenge);
        if (!operation) {
            operation = new ChallengeOperation(challenge, (value) => {
                this.onChallengeValidValue(challenge, value);
            }, (value) => {
                this.onChallengeInvalidValue(challenge, value);
            }, (response) => {
                this.onChallengeNonvalidatedSubmit(challenge, response);
                resolve(response);
            }, (response) => {
                this.onChallengeComplete(challenge, response);
                resolve(response);
            }, () => {
                this.onChallengeCancel(challenge);
                resolve(undefined);
            });
            this.challengeOperations[challenge.id] = operation;
        }
        return operation;
    }
    performOnObservers(challenge, perform) {
        const observers = this.challengeObservers[challenge.id] || [];
        for (const observer of observers) {
            perform(observer);
        }
    }
    onChallengeValidValue(challenge, value) {
        this.performOnObservers(challenge, (observer) => {
            var _a;
            (_a = observer.onValidValue) === null || _a === void 0 ? void 0 : _a.call(observer, value);
        });
    }
    onChallengeInvalidValue(challenge, value) {
        this.performOnObservers(challenge, (observer) => {
            var _a;
            (_a = observer.onInvalidValue) === null || _a === void 0 ? void 0 : _a.call(observer, value);
        });
    }
    onChallengeNonvalidatedSubmit(challenge, response) {
        this.performOnObservers(challenge, (observer) => {
            var _a;
            (_a = observer.onNonvalidatedSubmit) === null || _a === void 0 ? void 0 : _a.call(observer, response);
        });
    }
    onChallengeComplete(challenge, response) {
        this.performOnObservers(challenge, (observer) => {
            var _a;
            (_a = observer.onComplete) === null || _a === void 0 ? void 0 : _a.call(observer, response);
        });
    }
    onChallengeCancel(challenge) {
        this.performOnObservers(challenge, (observer) => {
            var _a;
            (_a = observer.onCancel) === null || _a === void 0 ? void 0 : _a.call(observer);
        });
    }
    getChallengeOperation(challenge) {
        return this.challengeOperations[challenge.id];
    }
    deleteChallengeOperation(operation) {
        const challenge = operation.challenge;
        challenge.customHandler = undefined;
        operation.deinit();
        delete this.challengeOperations[challenge.id];
    }
    cancelChallenge(challenge) {
        const operation = this.challengeOperations[challenge.id];
        operation.cancel();
        this.deleteChallengeOperation(operation);
    }
    completeChallenge(challenge) {
        const operation = this.challengeOperations[challenge.id];
        operation.complete();
        this.deleteChallengeOperation(operation);
    }
    async submitValuesForChallenge(challenge, values) {
        if (values.length === 0) {
            throw Error('Attempting to submit 0 values for challenge');
        }
        for (const value of values) {
            if (!value.prompt.validates) {
                const operation = this.getChallengeOperation(challenge);
                operation.addNonvalidatedValue(value);
            }
            else {
                const { valid, artifacts } = await this.validateChallengeValue(value);
                this.setValidationStatusForChallenge(challenge, value, valid, artifacts);
            }
        }
    }
    setValidationStatusForChallenge(challenge, value, valid, artifacts) {
        const operation = this.getChallengeOperation(challenge);
        operation.setValueStatus(value, valid, artifacts);
        if (operation.isFinished()) {
            this.deleteChallengeOperation(operation);
            const observers = this.challengeObservers[challenge.id] || [];
            observers.forEach(clearChallengeObserver);
            observers.length = 0;
            delete this.challengeObservers[challenge.id];
        }
    }
}
